import { LocalStorage, SessionStorage } from '../storage'
import {
  decrypt,
  decryptWithDetail,
  decryptWithKey,
  encryptWithDetail,
  encryptWithKey,
  importKey,
} from './encryptor'
import { logger } from '../logger'
import HDKeychain, { SerializedHDKeychain } from './keychains/HDKeychain'
import { KeychainType } from './keychains/IKeychain'
import KeyPairKeychain, { SerializedKeypairKeychain } from './keychains/KeyPairKeychain'

const VAULT_STORAGE_KEY = 'plug-vault'

const privates = new WeakMap()
let subscriber: SubscriberFunction | undefined = undefined

export type Keychain = HDKeychain | KeyPairKeychain

type SerializedKeychain = SerializedHDKeychain | SerializedKeypairKeychain
type DecryptedVault = SerializedKeychain[]
type SubscriberFunction = () => void | Promise<void>

interface KeychainManagerState {
  keychains: Keychain[]
  isUnlocked: boolean
  isInitiazed: boolean
  vault: string
}

class KeychainManager {
  state: KeychainManagerState

  constructor() {
    this.state = {
      keychains: [],
      isUnlocked: false,
      isInitiazed: false,
      vault: '',
    }

    privates.set(this, {
      password: '',
      rehydrate: async () => {
        try {
          const vault = await privates.get(this).getLastStorageState()
          if (vault) {
            this.state.vault = vault
          }

          const encryptionKey = await privates.get(this).getEncryptionKey()

          if (encryptionKey) {
            const key = await importKey(encryptionKey)
            const vault = (await decryptWithKey(
              key,
              JSON.parse(this.state.vault) as Awaited<ReturnType<typeof encryptWithKey>>
            )) as DecryptedVault

            await Promise.all(
              vault.map((serializedKeychain) => {
                return privates.get(this).restoreKeychain(serializedKeychain)
              })
            )
            await privates.get(this).persist()
            this.state.isUnlocked = true
          }
        } catch (e) {
          logger.debug('Failed to rehydrate keychain', { originalError: e })
        } finally {
          this.state.isInitiazed = true
          subscriber?.()
        }
      },
      setEncryptionKey: (val: string | null) => {
        return SessionStorage.set('plug-vault-encryption-key', val)
      },
      getEncryptionKey: () => {
        return SessionStorage.get('plug-vault-encryption-key')
      },
      setSalt: (val: string | null) => {
        return SessionStorage.set('plug-vault-salt', val)
      },
      getSalt: () => {
        return SessionStorage.get('plug-vault-salt')
      },
      getLastStorageState: () => {
        return LocalStorage.get(VAULT_STORAGE_KEY)
      },
      restoreKeychain: async (opts: SerializedKeychain): Promise<Keychain> => {
        let keychain
        switch (opts.type) {
          case KeychainType.HDKeychain:
            keychain = new HDKeychain()
            await keychain.init(opts)
            break
          case KeychainType.KeyPairKeychain:
            keychain = new KeyPairKeychain()
            await keychain.init(opts)
            break
        }
        this.state.keychains.push(keychain)
        return keychain
      },
      persist: async () => {
        // Serialize all the keychains
        let serializedKeychains = this.state.keychains?.length
          ? await Promise.all(this.state.keychains.map((keychain) => keychain.serialize()))
          : []

        // Remove any potential empty keychains
        serializedKeychains = serializedKeychains.filter((k) => !!k)

        // Encrypt the serialized keychains
        const pwd = privates.get(this).password
        const encryptionKey = await privates.get(this).getEncryptionKey()
        const salt = await privates.get(this).getSalt()

        if (serializedKeychains.length > 0) {
          const result = { vault: '', exportedKeyString: '', salt: '' }
          if (pwd) {
            // Generate a new encryption key every time we save and have a password
            const encryptionResult = await encryptWithDetail(
              privates.get(this).password as string,
              serializedKeychains
            )

            result.vault = encryptionResult.vault
            result.exportedKeyString = encryptionResult.exportedKeyString
            const vaultObj = JSON.parse(encryptionResult.vault) as Awaited<
              ReturnType<typeof decryptWithDetail>
            >
            result.salt = vaultObj.salt
          } else if (encryptionKey && salt) {
            const key = await importKey(encryptionKey)
            const vaultObj = await encryptWithKey(key, serializedKeychains)
            result.vault = JSON.stringify({ ...vaultObj, salt })
            result.salt = salt
            result.exportedKeyString = encryptionKey
          }
          this.state.vault = result.vault
          await privates.get(this).setEncryptionKey(result.exportedKeyString)
          await privates.get(this).setSalt(result.salt)
        } else {
          this.state.vault = ''
        }
        // Store them in the fs
        await LocalStorage.set(VAULT_STORAGE_KEY, this.state.vault)
      },
    })

    privates.get(this).rehydrate()
  }

  subscribe(subscriberFunction: SubscriberFunction) {
    subscriber = subscriberFunction
  }

  async setPassword(password: string) {
    privates.get(this).password = password
    this.state.isUnlocked = true
    await privates.get(this).persist()
    subscriber?.()
    return true
  }

  async verifyPassword(password: string) {
    try {
      // Check if we haven't set a password yet
      if (this.state.vault === '' && password === '' && this.state.keychains.length > 0) {
        return true
      }

      if (await decrypt(password, this.state.vault)) {
        return true
      }
      // eslint-disable-next-line no-empty
    } catch (e) {}
    return false
  }

  async hasVaultInStorage() {
    if (this.state.vault) return true

    const vault = await privates.get(this).getLastStorageState()
    return !!vault
  }

  async lock() {
    this.state.keychains = []
    this.state.isUnlocked = false
    privates.get(this).password = null
    await privates.get(this).setEncryptionKey(null)
    await privates.get(this).setSalt(null)
    subscriber?.()
  }

  async unlock(password: string) {
    if (!this.state.vault) {
      throw new Error('Nothing to unlock')
    }

    if (this.state.isUnlocked) return

    const {
      vault: decryptedVault,
      exportedKeyString,
      salt,
    } = await decryptWithDetail(password, this.state.vault)

    await privates.get(this).setEncryptionKey(exportedKeyString)
    await privates.get(this).setSalt(salt)
    privates.get(this).password = password
    this.state.isUnlocked = true

    await Promise.all(
      (decryptedVault as SerializedKeychain[]).map((serializedKeychain) => {
        return privates.get(this).restoreKeychain(serializedKeychain)
      })
    )
    await privates.get(this).persist()
    subscriber?.()
  }

  async addNewKeychain(opts?: SerializedKeychain): Promise<Keychain> {
    const keychain = new HDKeychain()
    await keychain.init(opts as SerializedHDKeychain)
    this.state.keychains.push(keychain)
    this.state.isUnlocked = true
    await privates.get(this).persist()
    subscriber?.()
    return keychain
  }

  async getPrincipals() {
    const keychainPrincipals = await Promise.all(
      (this.state.keychains || []).map((keychain) => keychain.getPrincipals())
    )

    return keychainPrincipals.flat()
  }

  async getKeychain(principal: string) {
    for (let i = 0; i < this.state.keychains.length; i++) {
      const keychain = this.state.keychains[i]
      const accounts = await keychain.getPrincipals()
      if (accounts.map((principal) => principal.toString()).includes(principal)) {
        return keychain
      }
    }
    throw new Error('No keychain found for account')
  }

  async getIdentity(principal: string) {
    const keychain = await this.getKeychain(principal)
    return keychain.getIdentity(principal)
  }

  async getSessionIdentity(principal: string, host: string) {
    const keychain = await this.getKeychain(principal)
    return keychain.getSessionIdentity(principal, host)
  }

  async addNewAccount(selectedKeychain: Keychain) {
    await selectedKeychain.addNewAccount()
    await privates.get(this).persist()
    subscriber?.()
    const principals = await selectedKeychain.getPrincipals()
    return principals[principals.length - 1]
  }

  async importKeychain(opts: SerializedKeychain): Promise<Keychain> {
    const result = await privates.get(this).restoreKeychain({
      ...opts,
      imported: true,
    })
    await privates.get(this).persist()
    subscriber?.()
    return result
  }

  async exportKeychain(principal: string, password: string) {
    const keychain = await this.getKeychain(principal)
    if (!(await this.verifyPassword(password))) {
      throw new Error('Wrong password')
    }
    return await keychain.exportKeychain()
  }

  async exportPem(principal: string, password: string) {
    const keychain = await this.getKeychain(principal)
    if (!(await this.verifyPassword(password))) {
      throw new Error('Wrong password')
    }
    return await keychain.exportPem(principal)
  }

  async clearKeychain() {
    await LocalStorage.clear()
    await SessionStorage.clear()

    this.state.keychains = []

    await privates.get(this).persist()
    subscriber?.()
  }
}

export default KeychainManager
